/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { Button, Row, Col, InputGroup, Form, Container, Modal, Card, Badge } from 'react-bootstrap';
import { connect } from 'react-redux';
import { buyTokenModalAction } from '../../store/token/tokenActions';
import { useParams, useLocation } from 'react-router-dom';
import { setLoadingAction, setLoadingFullAction } from '../../store/loading/loadingActions';
import { registerRequest, registerUpdateRequest, unregisterRequest } from '../../store/bot/botActions';
import { getBotByIdRequestAction, getRegisteredBotRequestAction } from '../../store/bot/botActions';
import MultiSignalsBackButton from '../MultiSignalsBackButton/MultiSignalsBackButton';
import './BotManagementDetailInputs.scss';

// Import modern icons
import {
    Settings,
    DollarSign,
    TrendingUp,
    Shield,
    AlertTriangle,
    Zap,
    Layers,
    CheckCircle,
    XCircle,
    Info,
    Rocket,
    RefreshCw,
    Trash2,
    AlertCircle,
    Lock,
    Activity,
    Target,
    PieChart
} from 'lucide-react';

function BotManagementDetailInputs({
    setLoadingFull,
    unregisterRequest,
    registerRequest,
    registerUpdateRequest,
    isEdit,
    user,
    getBotByIdRequest,
    registeredBotData,
    getRegisteredBotRequest,
    botDataById,
}) {
    const { id } = useParams();
    const location = useLocation();
    const [valueToBeMultiplied, setValueToBeMultiplied] = useState(0);
    const [valueToBeMultipliedParts, setValueToBeMultipliedParts] = useState(1);
    const [rangeValue, setRangeValue] = useState(0);
    const [showUnregisterPopup, setShowUnregisterPopup] = useState(false);
    const [exchangeName, setExchangeName] = useState('');
    const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [modalDisclaimerAccepted, setModalDisclaimerAccepted] = useState(false);
    const [riskLevel, setRiskLevel] = useState('Medium');
    const bot_id = id;
    const item = location.state?.item;

    // Risk level configurations with modern design
    const riskLevels = [
        {
            level: 'low',
            color: 'success',
            icon: Shield,
            gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            description: 'Conservative approach with minimal risk',
            features: ['Lower volatility', 'Stable returns', 'Capital preservation'],
            emoji: '🛡️'
        },
        {
            level: 'mid',
            color: 'warning',
            icon: AlertTriangle,
            gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            description: 'Balanced risk-reward strategy',
            features: ['Moderate volatility', 'Balanced returns', 'Growth focused'],
            emoji: '⚖️'
        },
        {
            level: 'high',
            color: 'danger',
            icon: Zap,
            gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            description: 'Aggressive strategy for maximum potential',
            features: ['Higher volatility', 'Maximum returns', 'Risk tolerant'],
            emoji: '🚀'
        }
    ];

    useEffect(() => {
        getRegisteredBotRequest();
        const payload = { bot_id };
        getBotByIdRequest(payload);
    }, [bot_id, getBotByIdRequest, getRegisteredBotRequest]);

    useEffect(() => {
        if (registeredBotData && Object.values(registeredBotData).length > 0) {
            const registeredBot = Object.values(registeredBotData).find(
                bot => bot.bot_id === parseInt(bot_id) && bot.registration_status === true
            );
            if (registeredBot) {
                setExchangeName(registeredBot.exchange || '');
                setValueToBeMultiplied(Math.round(parseFloat(registeredBot.investment) || 0));
                setValueToBeMultipliedParts(registeredBot.portion || 1);
                setRangeValue(Math.round(parseFloat(registeredBot.investment) || 0));
                setRiskLevel(registeredBot.risk || 'Medium');
                setDisclaimerAccepted(true);
            } else if (item) {
                const investment = parseFloat(item.investment) || 0;
                const portion = item.portion || 1;
                setExchangeName(item.exchange || '');
                setValueToBeMultiplied(Math.round(investment));
                setValueToBeMultipliedParts(portion);
                setRangeValue(Math.round(investment));
                setRiskLevel(item.risk || 'Medium');
            }
        } else if (item) {
            const investment = parseFloat(item.investment) || 0;
            const portion = item.portion || 1;
            setExchangeName(item.exchange || '');
            setValueToBeMultiplied(Math.round(investment));
            setValueToBeMultipliedParts(portion);
            setRangeValue(Math.round(investment));
            setRiskLevel(item.risk || 'Medium');
        }
    }, [registeredBotData, bot_id, item]);

    function getBotInvestment() {
        if (!isEdit || !registeredBotData || !exchangeName) return 0;

        const registeredBot = Object.values(registeredBotData).find(
            bot => bot.bot_id === parseInt(bot_id) &&
                bot.registration_status === true &&
                bot.exchange === exchangeName
        );

        return registeredBot ? parseFloat(registeredBot.investment) || 0 : 0;
    }

    function formatNumber(value) {
        if (typeof value !== 'number') {
            value = parseFloat(value);
        }
        return Math.round(value).toLocaleString('en-US').replace(/,/g, '.');
    }

    function parseNumber(value) {
        const stringValue = String(value);
        return parseFloat(stringValue.replace(/\./g, '').replace(/,/g, '.')) || 0;
    }

    function handleValueChange(e) {
        const inputValue = e.target.value;
        const numericValue = parseNumber(inputValue);
        const maxBalance = parseNumber(calculateMaxAllowedBalance());

        if (numericValue > maxBalance) {
            setValueToBeMultiplied(maxBalance);
            setRangeValue(maxBalance);
        } else {
            setValueToBeMultiplied(Math.round(numericValue));
            setRangeValue(Math.round(numericValue));
        }
    }

    function handleRangeChange(e) {
        const newValue = Math.round(parseFloat(e.target.value));
        setRangeValue(newValue);
        setValueToBeMultiplied(newValue);
    }

    function calculateBalance() {
        const selectedExchangeBalance = user?.[`${exchangeName}_balance`];
        const baseBalance = Number(selectedExchangeBalance || 0);

        if (isEdit) {
            const botInvestment = getBotInvestment();
            return (baseBalance + botInvestment).toFixed(0);
        }

        return baseBalance.toFixed(0);
    }

    function calculateBalanceFixed2() {
        const selectedExchangeBalance = user?.[`${exchangeName}_balance`];
        const baseBalance = Number(selectedExchangeBalance || 0);

        if (isEdit) {
            const botInvestment = getBotInvestment();
            return (baseBalance + botInvestment).toFixed(2);
        }

        return baseBalance.toFixed(2);
    }

    function calculateParts() {
        return botDataById?.max_portion || 1;
    }

    function calculateMaxAllowedBalance() {
        const balance = parseNumber(calculateBalance());
        if (valueToBeMultipliedParts === 1) {
            return Math.floor(balance * 0.85);
        }
        return balance;
    }

    const percentage = Math.min(((valueToBeMultiplied / parseNumber(calculateBalance())) * 100).toFixed(2), 100);
    const percentages = valueToBeMultipliedParts === 1 ? [25, 50, 75, 85] : [25, 50, 75, 100];

    const setBudgetPercentage = (percentage) => {
        const maxAllowed = parseNumber(calculateMaxAllowedBalance());
        const budget = (maxAllowed * percentage) / 100;
        const roundedBudget = Math.round(budget);
        setValueToBeMultiplied(roundedBudget);
        setRangeValue(roundedBudget);
    };

    const startStrategie = async (e) => {
        e.preventDefault();
        if (!disclaimerAccepted) {
            setShowDisclaimerModal(true);
            return;
        }

        setLoadingFull({ isLoading: true, loadingText: 'Waiting...' });
        const usdt = valueToBeMultiplied;
        const portion = valueToBeMultipliedParts;
        const status = true;
        const exchange = exchangeName;
        const risk = riskLevel;
        try {
            const payload = { bot_id, usdt, portion, status, exchange, risk };
            console.log(payload)
            await registerRequest(payload);
            setLoadingFull({ isLoading: false });
        } catch (err) {
            setLoadingFull({ isLoading: false });
        }
    };

    const updateStrategie = async (e) => {
        e.preventDefault();
        setLoadingFull({ isLoading: true, loadingText: 'Waiting...' });
        const usdt = valueToBeMultiplied;
        const portion = valueToBeMultipliedParts;
        const status = true;
        const exchange = exchangeName;
        const risk = riskLevel;
        try {
            const payload = { bot_id, usdt, portion, status, exchange, risk };
            await registerUpdateRequest(payload);
            setLoadingFull({ isLoading: false });
        } catch (err) {
            setLoadingFull({ isLoading: false });
        }
    };

    const unregisterBot = async () => {
        try {
            const payload = { bot_id };
            await unregisterRequest(payload);
            getRegisteredBotRequest();
            setExchangeName('');
            setValueToBeMultiplied(0);
            setRangeValue(0);
            setValueToBeMultipliedParts(1);
            setRiskLevel('Medium');
        } catch (err) {
            console.error('Unregister işlemi başarısız:', err);
        } finally {
            setShowUnregisterPopup(false);
        }
    };

    const handleDisclaimerAccept = () => {
        setDisclaimerAccepted(true);
        setModalDisclaimerAccepted(false);
        setShowDisclaimerModal(false);
    };

    const handleDisclaimerCancel = () => {
        setDisclaimerAccepted(false);
        setModalDisclaimerAccepted(false);
        setShowDisclaimerModal(false);
    };

    const handleExchangeChange = (e) => {
        const newExchange = e.target.value;
        setExchangeName(newExchange);
        setValueToBeMultiplied(0);
        setRangeValue(0);
    };

    const isRegisteredOnCurrentExchange = () => {
        return Object.values(registeredBotData || {}).some(
            bot => bot.exchange === exchangeName &&
                bot.bot_id === parseInt(bot_id) &&
                bot.registration_status === true
        );
    };

    const isRegistered = () => {
        return Object.values(registeredBotData || {}).some(
            bot => bot.bot_id === parseInt(bot_id) &&
                bot.registration_status === true
        );
    };

    // Modern Components
    const ModernCard = ({ children, className = '', gradient, icon: Icon, title }) => (
        <Card className={`modern-management-card ${className}`} style={gradient ? { background: gradient } : {}}>
            {title && (
                <Card.Header className="modern-card-header">
                    <div className="header-content">
                        {Icon && <Icon size={20} />}
                        <h6>{title}</h6>
                    </div>
                </Card.Header>
            )}
            <Card.Body className="modern-card-body">
                {children}
            </Card.Body>
        </Card>
    );

    const ModernButton = ({ children, variant = 'primary', size = 'md', icon: Icon, gradient, ...props }) => (
        <Button
            className={`modern-management-btn modern-btn-${variant} modern-btn-${size}`}
            style={gradient ? { background: gradient, border: 'none' } : {}}
            {...props}
        >
            {Icon && <Icon size={size === 'lg' ? 20 : 16} />}
            {children}
        </Button>
    );

    const RiskCard = ({ risk, isSelected, onClick }) => {
        const RiskIcon = risk.icon;
        return (
            <Card
                className={`modern-risk-card ${isSelected ? 'selected' : ''}`}
                onClick={onClick}
                style={isSelected ? { background: risk.gradient, color: 'white' } : {}}
            >
                <Card.Body className="text-center">
                    <div className="risk-icon-wrapper">
                        <div className="risk-emoji">{risk.emoji}</div>
                        <RiskIcon size={24} className="risk-icon" />
                    </div>
                    <h6 className="risk-title">{risk.level} Risk</h6>
                    <p className="risk-description">{risk.description}</p>
                    <ul className="risk-features">
                        {risk.features.map((feature, idx) => (
                            <li key={idx}>{feature}</li>
                        ))}
                    </ul>
                    {isSelected && (
                        <Badge bg="light" text="dark" className="selected-badge">
                            <CheckCircle size={14} className="me-1" />
                            Selected
                        </Badge>
                    )}
                </Card.Body>
            </Card>
        );
    };

    return (
        <div className="modern-bot-management">
            <Container>
                {/* Modern Back Button */}
                <div className="modern-back-section">
                    <MultiSignalsBackButton buttonText="Back" />
                </div>

                {/* Modern Header */}
                <div className="modern-management-header">
                    <div className="header-icon">
                        <Settings size={40} />
                    </div>
                    <div className="header-text">
                        <h1>Bot Management</h1>
                        <p>Configure your trading bot settings and risk preferences</p>
                    </div>
                </div>

                <Row className="justify-content-center">
                    <Col lg={10} xl={8}>
                        <div className="management-container">
                            {/* Exchange Selection */}
                            <ModernCard title="Exchange Configuration" icon={Activity}>
                                <div className="exchange-selection">
                                    <label className="modern-label">
                                        <Target size={16} />
                                        Select Exchange
                                    </label>
                                    <Form.Select
                                        className="modern-select"
                                        value={exchangeName}
                                        onChange={handleExchangeChange}
                                        size="lg"
                                    >
                                        <option value="">Choose your preferred exchange</option>
                                        {botDataById?.exchanges?.map((exchange, index) => {
                                            const balance = user?.[`${exchange.name}_balance`];
                                            const hasBalance = balance && Number(balance) > 0;

                                            return (
                                                <option
                                                    key={index}
                                                    value={exchange.name}
                                                    disabled={!hasBalance}
                                                >
                                                    {exchange.name.charAt(0).toUpperCase() + exchange.name.slice(1).toLowerCase()}
                                                    {!hasBalance ? ' (No Balance)' : ` ($${Number(balance || 0).toFixed(2)})`}
                                                </option>
                                            );
                                        })}
                                    </Form.Select>

                                    {exchangeName && user && Number(user[`${exchangeName}_balance`] || 0) <= 0 && (
                                        <div className="modern-alert alert-warning">
                                            <AlertCircle size={16} />
                                            <span>Selected exchange ({exchangeName}) has insufficient balance.</span>
                                        </div>
                                    )}
                                </div>
                            </ModernCard>

                            {exchangeName === "" ? (
                                <div className="empty-state">
                                    <div className="empty-icon">
                                        <Activity size={64} />
                                    </div>
                                    <h3>Please select an exchange to continue</h3>
                                    <p>Choose from your available exchanges with positive balance</p>
                                </div>
                            ) : (
                                <div className="configuration-sections">
                                    {/* Budget Configuration */}
                                    <ModernCard
                                        title="Budget Configuration"
                                        icon={DollarSign}
                                        className="budget-card"
                                    >
                                        <div className="budget-section">
                                            <div className="investment-input">
                                                <label className="modern-label">
                                                    <DollarSign size={16} />
                                                    Investment Amount
                                                </label>
                                                <InputGroup className="modern-input-group">
                                                    <Form.Control
                                                        className="modern-input"
                                                        type="text"
                                                        value={formatNumber(valueToBeMultiplied)}
                                                        onChange={handleValueChange}
                                                        size="lg"
                                                    />
                                                    <InputGroup.Text className="modern-input-addon">
                                                        $
                                                    </InputGroup.Text>
                                                </InputGroup>
                                            </div>

                                            <div className="range-section">
                                                <Form.Range
                                                    className="modern-range"
                                                    max={parseNumber(calculateMaxAllowedBalance())}
                                                    min={0}
                                                    value={rangeValue}
                                                    onChange={handleRangeChange}
                                                />

                                                <div className="range-info">
                                                    <span className="available-balance">
                                                        Available: ${calculateBalanceFixed2()}
                                                    </span>
                                                    <Badge bg="primary" className="percentage-badge">
                                                        {percentage}%
                                                    </Badge>
                                                </div>
                                            </div>

                                            <div className="percentage-buttons">
                                                {percentages.map((percent, index) => (
                                                    <Button
                                                        key={index}
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="percentage-btn"
                                                        onClick={() => setBudgetPercentage(percent)}
                                                    >
                                                        {percent}%
                                                    </Button>
                                                ))}
                                            </div>

                                            {valueToBeMultipliedParts === 1 && (
                                                <div className="modern-alert alert-info">
                                                    <Info size={16} />
                                                    <span>For single lot trading, maximum 85% of balance can be used for safety.</span>
                                                </div>
                                            )}
                                        </div>
                                    </ModernCard>

                                    {/* Position Management */}
                                    <ModernCard
                                        title="Position Management"
                                        icon={Layers}
                                        className="position-card"
                                    >
                                        <div className="position-section">
                                            <label className="modern-label">
                                                <PieChart size={16} />
                                                Investment Division ({valueToBeMultipliedParts}/{calculateParts()})
                                            </label>
                                            <Form.Range
                                                className="modern-range"
                                                step="1"
                                                max={Number(calculateParts())}
                                                min={1}
                                                value={valueToBeMultipliedParts}
                                                onChange={(e) => setValueToBeMultipliedParts(parseInt(e.target.value))}
                                            />
                                            <div className="position-info">
                                                <div className="parts-info">
                                                    <span>Parts: {valueToBeMultipliedParts}</span>
                                                    <Badge bg="info">
                                                        ${Math.round(valueToBeMultiplied / valueToBeMultipliedParts)} per part
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    </ModernCard>

                                    {/* Risk Management */}
                                    <ModernCard
                                        title="Risk Management"
                                        icon={Shield}
                                        className="risk-management-card"
                                    >
                                        <div className="risk-selection">
                                            <label className="modern-label">
                                                <Shield size={16} />
                                                Select Risk Level
                                            </label>
                                            <Row>
                                                {riskLevels.map((risk, index) => (
                                                    <Col md={4} key={index} className="mb-3">
                                                        <RiskCard
                                                            risk={{
                                                                ...risk,
                                                                level: risk.level.charAt(0).toUpperCase() + risk.level.slice(1).toLowerCase()
                                                            }}
                                                            isSelected={riskLevel === risk.level}
                                                            onClick={() => setRiskLevel(risk.level)}
                                                        />
                                                    </Col>
                                                ))}
                                            </Row>

                                        </div>
                                    </ModernCard>

                                    {/* Disclaimer */}
                                    {!isRegistered() && (
                                        <ModernCard className="disclaimer-card">
                                            <Form.Check
                                                type="checkbox"
                                                className="modern-checkbox"
                                                label="I have read and understood the Multisignals API Disclaimer and Terms of Use."
                                                checked={disclaimerAccepted}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setShowDisclaimerModal(true);
                                                    } else {
                                                        setDisclaimerAccepted(false);
                                                    }
                                                }}
                                            />
                                        </ModernCard>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="action-section">
                                        {isEdit ? (
                                            <ModernButton
                                                variant="primary"
                                                size="lg"
                                                icon={RefreshCw}
                                                gradient="linear-gradient(135deg, #097875 0%, #764ba2 100%)"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateStrategie(e);
                                                }}
                                                disabled={
                                                    valueToBeMultiplied > parseNumber(calculateMaxAllowedBalance()) ||
                                                    (!disclaimerAccepted && !isRegistered()) ||
                                                    valueToBeMultiplied <= 0
                                                }
                                            >
                                                {isRegisteredOnCurrentExchange() ? 'Update Configuration' : 'Register Bot'}
                                            </ModernButton>
                                        ) : (
                                            <ModernButton
                                                variant="primary"
                                                size="lg"
                                                icon={isRegisteredOnCurrentExchange() ? CheckCircle : Rocket}
                                                gradient={isRegisteredOnCurrentExchange()
                                                    ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                                    : "linear-gradient(135deg, #097875 0%, #764ba2 100%)"
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    isRegistered() ? updateStrategie(e) : startStrategie(e);
                                                }}
                                                disabled={
                                                    valueToBeMultiplied > parseNumber(calculateMaxAllowedBalance()) ||
                                                    (!disclaimerAccepted && !isRegistered()) ||
                                                    rangeValue <= 0 ||
                                                    (isRegisteredOnCurrentExchange() && !isEdit)
                                                }
                                            >
                                                <span className='m-2'>{isRegisteredOnCurrentExchange() ? 'Already Registered' :
                                                    isRegistered() ? 'Update Configuration' : 'Start Trading Bot'}</span>
                                            </ModernButton>
                                        )}

                                        {isRegisteredOnCurrentExchange() && (
                                            <ModernButton
                                                variant="danger"
                                                size="lg"
                                                icon={Trash2}
                                                gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                                                onClick={() => setShowUnregisterPopup(true)}
                                            >
                                                Unregister Bot
                                            </ModernButton>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* Disclaimer Modal */}
                <Modal
                    show={showDisclaimerModal}
                    onHide={handleDisclaimerCancel}
                    dialogClassName="modal-lg"
                    centered
                    className="modern-modal"
                >
                    <Modal.Header closeButton className="modern-modal-header">
                        <Modal.Title>
                            <Lock size={24} className="me-2" />
                            Multisignals API Disclaimer and Terms of Use
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="modern-modal-body">
                        <div className="disclaimer-content">
                            <div className="modern-alert alert-warning">
                                <AlertTriangle size={16} />
                                <strong>Important:</strong> Please read all terms carefully before proceeding.
                            </div>

                            <p className="disclaimer-intro">
                                By using the Multisignals API services, you acknowledge and agree to the following terms and conditions:
                            </p>

                            <div className="terms-content">
                                <div className="term-section">
                                    <h6><Lock size={16} /> 1. API Key Usage and Responsibility</h6>
                                    <p>Your API Key allows access to Multisignals services, including automated trading and data retrieval. You are fully responsible for securing your API Key and ensuring it is not accessed by unauthorized individuals or systems.</p>
                                </div>

                                <div className="term-section">
                                    <h6><TrendingUp size={16} /> 2. Market Risks</h6>
                                    <p>Cryptocurrency trading is inherently risky due to significant market volatility. Utilizing API services for automation does not eliminate risks or guarantee positive results.</p>
                                </div>

                                <div className="term-section">
                                    <h6><Shield size={16} /> 3. Security and Permissions</h6>
                                    <p>Ensure your API Key permissions are configured correctly (e.g., read-only, trading, or withdrawal). Grant only the access levels required for your intended operations.</p>
                                </div>

                                <div className="term-section">
                                    <h6><Activity size={16} /> 4. Service Availability and Limitations</h6>
                                    <p>Multisignals provides API services "as-is" and does not guarantee uninterrupted access or the accuracy of data provided.</p>
                                </div>

                                <div className="term-section">
                                    <h6><AlertCircle size={16} /> 5. Compliance and Legal Responsibility</h6>
                                    <p>By using Multisignals API services, you confirm that you comply with all relevant laws and regulations in your jurisdiction.</p>
                                </div>
                            </div>

                            <div className="acceptance-section">
                                <Form.Check
                                    type="checkbox"
                                    className="modern-checkbox"
                                    label="I acknowledge the risks associated with automated trading and agree to proceed at my own responsibility."
                                    onChange={(e) => setModalDisclaimerAccepted(e.target.checked)}
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="modern-modal-footer">
                        <Button variant="outline-secondary" onClick={handleDisclaimerCancel}>
                            <XCircle size={16} className="me-1" />
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleDisclaimerAccept}
                            disabled={!modalDisclaimerAccepted}
                        >
                            <CheckCircle size={16} className="me-1" />
                            Agree and Continue
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Unregister Confirmation Modal */}
                <Modal
                    show={showUnregisterPopup}
                    onHide={() => setShowUnregisterPopup(false)}
                    centered
                    className="modern-modal"
                >
                    <Modal.Header closeButton className="modern-modal-header danger">
                        <Modal.Title>
                            <AlertTriangle size={24} className="me-2" />
                            Confirm Bot Unregistration
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="modern-modal-body">
                        <p>You are about to unregister from this trading bot.</p>
                        <div className="modern-alert alert-danger">
                            <AlertCircle size={16} />
                            <div>
                                <strong>Please note:</strong> The bot will not open any new trades, but it will continue to manage your existing open positions until they are closed.
                            </div>
                        </div>
                        <p>Do you wish to proceed with unregistration?</p>
                    </Modal.Body>
                    <Modal.Footer className="modern-modal-footer">
                        <Button variant="outline-secondary" onClick={() => setShowUnregisterPopup(false)}>
                            <XCircle size={16} className="me-1" />
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={unregisterBot}>
                            <Trash2 size={16} className="me-1" />
                            Unregister Bot
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </div>
    );
}

const mapStateToProps = (state) => ({
    user: state.userReducer.user,
    botDataById: state.botReducer.botDataById,
    registeredBotData: state.botReducer.registeredBotData,
});

const mapDispatchToProps = (dispatch) => ({
    buyTokenModalRequest: (payload) => dispatch(buyTokenModalAction(payload)),
    setLoading: (payload) => dispatch(setLoadingAction(payload)),
    setLoadingFull: (payload) => dispatch(setLoadingFullAction(payload)),
    registerRequest: (creds) => dispatch(registerRequest(creds)),
    registerUpdateRequest: (creds) => dispatch(registerUpdateRequest(creds)),
    unregisterRequest: (creds) => dispatch(unregisterRequest(creds)),
    getBotByIdRequest: (payload) => dispatch(getBotByIdRequestAction(payload)),
    getRegisteredBotRequest: () => dispatch(getRegisteredBotRequestAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BotManagementDetailInputs);